/** Dashboard widget: overall API/database health and process uptime. */

import React, { useEffect, useState } from "react";
import { apiClient } from "@/api/client";
import type { SystemHealth } from "@/types";
import WidgetCard from "./WidgetCard";

function formatUptime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export default function APIHealthWidget(): React.ReactElement {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get<SystemHealth>("/system/health")
      .then(({ data }) => setHealth(data))
      .catch(() => setError("API unreachable"));
  }, []);

  return (
    <WidgetCard title="API health" isLoading={health === null && !error} error={error}>
      {health && (
        <>
          <div className="flex items-center gap-2">
            <span
              className={`h-2.5 w-2.5 rounded-full ${health.database_connected ? "bg-signal-teal" : "bg-signal-rose"}`}
            />
            <p className="text-sm capitalize text-mist-200">{health.status}</p>
          </div>
          <p className="text-xs text-mist-400">
            v{health.version} · up {formatUptime(health.uptime_seconds)}
          </p>
        </>
      )}
    </WidgetCard>
  );
}
